import { Container } from 'unstated';

import _ from 'lodash';

const defaultAddresses = (addresses) =>
  _.sortBy(
    _.unionBy(
      addresses,
      [
        { addressTypeId: 1, addressTypeDescription: 'SHIPPING' },
        { addressTypeId: 2, addressTypeDescription: 'BILLING' }
      ],
      'addressTypeId'
    ),
    'addressTypeId'
  );

class ProfileContainer extends Container {
  constructor(serviceContainer, props = {}) {
    super(props);

    this.serviceContainer = serviceContainer;
    this.accountService = serviceContainer.accountService;
    this.customerService = serviceContainer.customerService;
    this.presenceService = serviceContainer.presenceService;
    this.saleHistoryService = serviceContainer.saleHistoryService;
    this.wineMetadataService = serviceContainer.wineMetadataService;

    this.state = {
      details: undefined,
      passwordResetAttempt: undefined,
      currentProfileId: undefined
    };
  }

  getDetails() {
    return this.state.details;
  }

  getCustomerId() {
    return this.getDetails() ? this.getDetails().id : null;
  }

  getPrimaryProfile() {
    if (_.isNil(this.getDetails())) return null;

    return _.find(this.getDetails().profiles, { primary: true });
  }

  getCurrentProfile() {
    if (_.isNil(this.getDetails())) return null;

    return _.find(this.getDetails().profiles, {
      id: this.presenceService.getCurrentProfileId()
    });
  }

  getSelectableProfiles() {
    if (_.isNil(this.getDetails())) return null;

    return _.filter(
      this.getDetails().profiles,
      (profile) => profile.id !== this.presenceService.getCurrentProfileId()
    );
  }

  setCurrentProfileId(profileId) {
    return Promise.resolve(
      this.presenceService.setCurrentProfileId(profileId)
    ).then(() => this.setState({ currentProfileId: profileId }));
  }

  getPasswordResetAttempt() {
    return this.state.passwordResetAttempt;
  }

  isIdentified() {
    return this.presenceService.isIdentified();
  }

  isAuthenticated() {
    return this.presenceService.isAuthenticated();
  }

  refreshDetails() {
    return new Promise((resolve) => {
      return this.accountService
        .getDetails()
        .then((data) => this.setState({ details: data }).then(resolve))
        .catch(() => this.logOut().then(resolve));
    });
  }

  logIn(email, password) {
    const wasPreviouslyIdentified = this.presenceService.isIdentified();

    return this.accountService.logIn(email, password).then((data) => {
      return this.setState({ details: data }).then(() => {
        return this.setCurrentProfileId(this.getPrimaryProfile().id).then(
          () => {
            this.presenceService.setCustomerId(data.id);
            this.presenceService.setIdentified(data.token);
            this.presenceService.setAuthenticated(data.token);

            return wasPreviouslyIdentified;
          }
        );
      });
    });
  }

  // Whoever is signed in is signed out first, so a failed token leaves no one signed in.
  impersonate(token) {
    return this.logOut()
      .then(() => this.accountService.impersonate(token))
      .then((data) => {
        return this.setState({ details: data }).then(() => {
          return this.setCurrentProfileId(this.getPrimaryProfile().id).then(
            () => {
              this.presenceService.setCustomerId(data.id);
              this.presenceService.setIdentified(data.token);
              this.presenceService.setAuthenticated(data.token);
            }
          );
        });
      });
  }

  logOut() {
    this.presenceService.clear();
    this.wineMetadataService.purge();

    return this.setState({ details: undefined });
  }

  resetPassword(email) {
    return this.accountService
      .resetPassword(email)
      .then(() => this.setState({ passwordResetAttempt: email }))
      .then(() => this.logOut());
  }

  updatePassword(resetToken, password) {
    return this.accountService
      .updatePassword(resetToken, password)
      .then(() => this.setState({ passwordResetAttempt: undefined }))
      .then(() => this.logOut());
  }

  changePassword(password, currentPassword) {
    return this.accountService.changePassword(password, currentPassword);
  }

  createProfile(values) {
    return this.accountService
      .createProfile(values)
      .then((data) => this.setState({ details: data }));
  }

  updateProfile(values) {
    return this.accountService
      .updateProfile(this.getCurrentProfile().id, values)
      .then((data) => this.setState({ details: data }));
  }

  isCurrentProfilePrimary() {
    const current = this.getCurrentProfile();

    return current && current.primary;
  }

  canCreateProfile() {
    return this.getDetails() ? _.size(this.getDetails().profiles) < 2 : false;
  }

  uploadAvatar(file) {
    return this.accountService
      .uploadAvatar(this.getCurrentProfile().id, file)
      .then((data) => this.setState({ details: data }));
  }

  getAddresses() {
    if (_.isNil(this.getDetails())) return defaultAddresses([]);

    return defaultAddresses(this.getDetails().addresses);
  }

  getBillingAddress() {
    return _.find(this.getAddresses(), { addressTypeId: 2 });
  }

  getShippingAddress() {
    return _.find(this.getAddresses(), { addressTypeId: 1 });
  }

  createAddress(values) {
    return this.accountService
      .createAddress(values)
      .then((data) => this.setState({ details: data }));
  }

  updateAddress(id, values) {
    return this.accountService
      .updateAddress(id, values)
      .then((data) => this.setState({ details: data }));
  }

  updatePaymentMethod(values) {
    return this.accountService
      .updatePaymentMethod(values)
      .then((data) => this.setState({ details: data }));
  }

  submitHelpRequest(values) {
    return this.accountService.submitHelpRequest(values);
  }

  getReferralFactory() {
    return this.customerService.fetchReferralFactory();
  }
}

export { ProfileContainer };
